import { createContext, useContext, useEffect, useMemo, useState } from "react";
import languagesService from "services/languages-service";
import { useMaterialUIController } from "./auth.context";
import moment from "moment";
import "moment/locale/ar";

const LanguageContext = createContext();
const defaultExpressions = [
  {
    code: "audit-plural",
    content: "Audits",
  },
  {
    code: "audit-singular",
    content: "Audit",
  },
  {
    code: "add-new-audit-button",
    content: "Add New Audit",
  },
  { code: "export-button", content: "Export" },
  {
    code: "search-by-label",
    content: "Search by",
  },
  {
    code: "branch-plural",
    content: "Branches",
  },
  {
    code: "branch-singular",
    content: "Branch",
  },
  {
    code: "branch-group-plural",
    content: "Branch groups",
  },
  {
    code: "branch-group-singular",
    content: "Branch group",
  },
  {
    code: "user-plural",
    content: "Users",
  },
  {
    code: "user-singular",
    content: "User",
  },
  {
    code: "search-label",
    content: "Search",
  },
  {
    code: "filter-by-date-label",
    content: "Filter by date",
  },
  {
    code: "filter-by-status-label",
    content: "Filter by status",
  },
  {
    code: "filter-by-region-label",
    content: "Filter by region",
  },
  {
    code: "filter-by-assignedto-label",
    content: "Filter by assigned to",
  },
  {
    code: "from-date-label",
    content: "From",
  },
  {
    code: "to-date-label",
    content: "To",
  },
  {
    code: "description-singular",
    content: "Description",
  },
  {
    code: "description-plural",
    content: "Descriptions",
  },
  {
    code: "date-singular",
    content: "Date",
  },
  {
    code: "date-plural",
    content: "Dates",
  },
  {
    code: "assigned-to-singular",
    content: "Assigned to",
  },
  {
    code: "assigned-to-plural",
    content: "Assigned tos",
  },
  {
    code: "status-singular",
    content: "Status",
  },
  {
    code: "status-plural",
    content: "Statuses",
  },
  {
    code: "create-internal-audit-button",
    content: "For my orgnanization",
  },
  {
    code: "create-external-audit-button",
    content: "For my clients",
  },
  { code: "choose-button", content: "Choose" },
  { code: "footer", content: "© 2024 | Crafted with ❤ by Coderaas " },
];

export default function LanguageContextProvider({ children }) {
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState({
    id: localStorage.getItem("language") || "en",
    rtl: false,
  });
  const [expressions, setExpressions] = useState(defaultExpressions);
  const [loading, setLoading] = useState(true);
  const [, dispatch] = useMaterialUIController();

  async function getLanguages() {
    setLoading(true);
    const [response, data] = await languagesService.getMultipleLanguages();
    if (response.ok) {
      setLanguages(data);
      setLanguage(data.find((lang) => lang.id == language.id));
    }
  }

  async function getExpressions(languageCode) {
    const [response, data] =
      await languagesService.getMultipleExpressions(languageCode);
    if (response.ok) {
      setExpressions(data);
    }
    setLoading(false);
  }

  async function translate(from, to, content) {
    return await languagesService.translate({ from, to, content });
  }

  async function changeLanguage(languageId) {
    setLanguage(languages.find((l) => l.id == languageId));
    localStorage.setItem("language", languageId);
    window.location.reload();
  }

  const labels = useMemo(() => {
    if (!expressions) {
      return {};
    }

    const labels = {};
    expressions.forEach((expression) => {
      if (!expression.typeId || expression.typeId == 1) {
        labels[expression.code] = expression.content;
      }
    });

    return labels;
  }, [expressions]);

  useEffect(() => {
    getLanguages();
  }, []);

  useEffect(() => {
    if (!language?.id) {
      return;
    }

    moment.locale(language.id);

    localStorage.setItem("language", language.id);
    getExpressions(language.id);
  }, [language?.id]);

  useEffect(() => {
    dispatch({ type: "DIRECTION", value: language?.rtl ? "rtl" : "ltr" });
  }, [!!language?.rtl]);

  const providerValue = useMemo(() => {
    return {
      language,
      languageId: language?.id,
      languages,
      labels,
      label: (key) => labels?.[key.replaceAll(" ", "-").toLowerCase()] || "",
      isRTL: language?.rtl,
      translate,
      changeLanguage,
    };
  }, [language, languages, labels, changeLanguage]);

  return (
    <LanguageContext.Provider value={providerValue}>
      {loading ? "" : children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
